import {
  REDACTED,
  redactJsonField,
  redactPayloadField,
  SENSITIVE_URL_PARAM_PREFIXES,
  SENSITIVE_URL_PARAMS,
} from '../sensitiveFieldKeys';

const URL_PARSE_BASE = 'https://redaction.invalid';
// Bound recursion for malformed or repeatedly nested `url` parameters.
const MAX_NESTED_URL_DEPTH = 3;
// Netron passes an artifact's signed URL through its own `url` query parameter.
const NESTED_URL_PARAMS = new Set(['url']);
// `signature` is globally redacted; these fields require a legacy signed URL.
const LEGACY_SIGNED_URL_ADDITIONAL_PARAMS = new Set([
  'awsaccesskeyid',
  'googleaccessid',
  'expires',
]);
const AZURE_SAS_SCOPE_PARAMS = ['sp', 'se', 'sr', 'ss', 'srt', 'skoid'];

/**
 * An Azure Blob SAS is a bearer token spread across several short, unprefixed
 * query parameters. Once the URL is identified as a SAS, redact the complete
 * token: even fields that are not secrets alone expose its access scope,
 * validity, principal IDs, or other values covered by the signature. `sig`
 * itself is already covered by SENSITIVE_URL_PARAMS.
 */
const AZURE_SAS_ADDITIONAL_PARAMS = new Set([
  'sv',
  ...AZURE_SAS_SCOPE_PARAMS,
  // Validity and request restrictions shared by SAS variants.
  'st',
  'sip',
  'spr',
  'si',
  // User delegation key and principal metadata.
  'sktid',
  'skt',
  'ske',
  'sks',
  'skv',
  'saoid',
  'suoid',
  'scid',
  'sdd',
  'ses',
  'skdutid',
  'sduoid',
  // Signed request constraints supported by newer storage service versions.
  'srh',
  'srq',
  // Response header overrides included in the SAS string-to-sign.
  'rscc',
  'rscd',
  'rsce',
  'rscl',
  'rsct',
]);

interface SignedUrlFormats {
  legacy: boolean;
  azureSas: boolean;
}

/** Detects signatures by parameter shape rather than host to support custom storage domains. */
function detectSignedUrlFormats(params: URLSearchParams): SignedUrlFormats {
  return {
    legacy:
      params.has('Signature') &&
      (params.has('AWSAccessKeyId') || params.has('GoogleAccessId')),
    // Require the signature, version, and a scope field to avoid treating
    // unrelated short query parameters such as `sig` or `sv` as a SAS.
    azureSas:
      params.has('sig') &&
      params.has('sv') &&
      AZURE_SAS_SCOPE_PARAMS.some(param => params.has(param)),
  };
}

function isSensitiveParam(
  param: string,
  signedUrlFormats: SignedUrlFormats
): boolean {
  return (
    SENSITIVE_URL_PARAMS.has(param) ||
    SENSITIVE_URL_PARAM_PREFIXES.some(prefix => param.startsWith(prefix)) ||
    (signedUrlFormats.legacy &&
      LEGACY_SIGNED_URL_ADDITIONAL_PARAMS.has(param)) ||
    (signedUrlFormats.azureSas && AZURE_SAS_ADDITIONAL_PARAMS.has(param))
  );
}

interface RumEventWithUrls {
  view: {
    url: string;
    referrer?: string;
    performance?: {lcp?: {resource_url?: string}};
  };
  action?: {
    target?: {name: string; [key: string]: unknown};
    [key: string]: unknown;
  };
  resource?: {url?: string};
  error?: {resource?: {url?: string}};
  long_task?: {scripts?: Array<{source_url?: string}>};
}

/**
 * Returns the URL with sensitive application and cloud-signed query values
 * replaced by REDACTED. Relative URLs are parsed against a non-routable base
 * and returned in their original relative form.
 */
export function redactSensitiveUrlParams(raw: string): string;
export function redactSensitiveUrlParams(
  raw: string | undefined
): string | undefined;
export function redactSensitiveUrlParams(
  raw: string | undefined
): string | undefined {
  return redactSensitiveUrlParamsAtDepth(raw, 0);
}

function redactSensitiveUrlParamsAtDepth(
  raw: string | undefined,
  depth: number
): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const url = new URL(raw, URL_PARSE_BASE);
    const signedUrlFormats = detectSignedUrlFormats(url.searchParams);
    let changed = false;

    for (const [key, value] of url.searchParams) {
      const lowerKey = key.toLowerCase();

      if (isSensitiveParam(lowerKey, signedUrlFormats)) {
        url.searchParams.set(key, REDACTED);
        changed = true;
      } else if (
        depth < MAX_NESTED_URL_DEPTH &&
        NESTED_URL_PARAMS.has(lowerKey)
      ) {
        const redactedValue = redactSensitiveUrlParamsAtDepth(value, depth + 1);
        if (redactedValue !== value) {
          url.searchParams.set(key, redactedValue ?? value);
          changed = true;
        }
      }
    }

    if (!changed) {
      return raw;
    }
    if (/^[a-z][a-z\d+.-]*:/i.test(raw)) {
      return url.toString();
    }
    if (raw.startsWith('//')) {
      return `//${url.host}${url.pathname}${url.search}${url.hash}`;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return raw;
  }
}

/** Scrubs every URL field that Datadog permits beforeSend to modify. */
export function redactRumEventUrls(event: RumEventWithUrls): void {
  event.view.url = redactSensitiveUrlParams(event.view.url);
  const actionTarget = event.action?.target;
  if (typeof actionTarget?.url === 'string') {
    actionTarget.url = redactSensitiveUrlParams(actionTarget.url);
  }
  if (event.view.referrer) {
    event.view.referrer = redactSensitiveUrlParams(event.view.referrer);
  }
  const lcp = event.view.performance?.lcp;
  if (lcp?.resource_url) {
    lcp.resource_url = redactSensitiveUrlParams(lcp.resource_url);
  }
  if (event.resource?.url) {
    event.resource.url = redactSensitiveUrlParams(event.resource.url);
  }
  if (event.error?.resource?.url) {
    event.error.resource.url = redactSensitiveUrlParams(
      event.error.resource.url
    );
  }
  for (const script of event.long_task?.scripts ?? []) {
    if (script.source_url) {
      script.source_url = redactSensitiveUrlParams(script.source_url);
    }
  }
}

/**
 * Scrubs sensitive fields from GraphQL resource events before they're sent to
 * Datadog. Redacts variables matching SENSITIVE_KEYS in graphql.variables,
 * graphql.payload, and context._dd.graphql.variables.
 */
export function redactGraphQlSensitiveFields(event: any) {
  const graphql = event.resource?.graphql;
  if (!graphql) {
    return;
  }

  graphql.variables = redactJsonField(graphql.variables);
  graphql.payload = redactPayloadField(graphql.payload);

  const ddKey = '_dd.graphql.variables';
  if (event.context?.[ddKey]) {
    event.context[ddKey] = redactJsonField(event.context[ddKey]);
  }
}
